/**
 * Shared Cost Section Internal Component
 *
 * Internal component for displaying API cost breakdowns.
 * Used across AuditTab.tsx, KeywordEngineTestTab.tsx, ScreenshotEngineTestTab.tsx, etc.
 * This is an internal/debug component (not user-facing).
 */

import { Subheading, Text, Strong } from "@a7y/tailwind-catalyst";
import { DevCardBadge } from "./DevCardBadge";

// ======================================================================================|
// MARK: Types

export interface CostProviderOpenAI {
  total_tokens: number;
  total_cost_in_currency: number;
  requests: Array<{
    model_name: string;
    total_tokens: number;
    tokens_in: number;
    tokens_out: number;
    cost_in_currency: number;
  }>;
}

export interface CostProviderGemini {
  total_tokens: number;
  total_cost_in_currency: number;
  requests: Array<{
    model_name: string;
    total_tokens: number;
    tokens_in: number;
    tokens_out: number;
    cost_in_currency: number;
  }>;
}

export interface CostProviderGoogleTrends {
  total_requests: number;
  total_cost_in_currency: number;
  requests_by_endpoint?: Array<{
    endpoint_name: string;
    count: number;
    cost_in_currency: number;
  }>;
}

/** @deprecated Use CostProviderGoogleTrends */
export type CostProviderSearchAPI = CostProviderGoogleTrends;

/** Base cost data without engine breakdown */
export interface BaseCostData {
  openai?: CostProviderOpenAI;
  gemini?: CostProviderGemini;
  /** @deprecated Use google_trends */
  searchapi?: CostProviderGoogleTrends;
  google_trends?: CostProviderGoogleTrends;
}

/** Sub-engine identifier for audit cost breakdown */
export type AuditSubEngineId =
  | "icon"
  | "competitor"
  | "keyword"
  | "screenshot"
  | "sentiment"
  | "primaryUseCase"
  | "anchoredBrief";

/** Human-readable labels for sub-engines */
export const AUDIT_SUB_ENGINE_LABELS: Record<AuditSubEngineId, string> = {
  icon: "Icon Engine",
  competitor: "Competitor Discovery Engine",
  keyword: "Keyword Engine",
  screenshot: "Screenshot Engine",
  sentiment: "Sentiment Engine",
  primaryUseCase: "Primary Use Case Analysis",
  anchoredBrief: "Anchored Brief Generator",
};

/** Per-engine cost breakdown */
export type AuditSubEngineCosts = Partial<Record<AuditSubEngineId, BaseCostData>>;

/** Full cost data including optional engine breakdown */
export interface CostData extends BaseCostData {
  engines?: AuditSubEngineCosts;
}

// ======================================================================================|
// MARK: Helpers

/**
 * Calculates the total cost from a BaseCostData object.
 */
export function calculateTotalCost(cost: BaseCostData): number {
  const openaiCost = cost.openai?.total_cost_in_currency ?? 0;
  const geminiCost = cost.gemini?.total_cost_in_currency ?? 0;
  const searchapiCost = cost.searchapi?.total_cost_in_currency ?? 0;
  const googleTrendsCost = cost.google_trends?.total_cost_in_currency ?? 0;
  return openaiCost + geminiCost + searchapiCost + googleTrendsCost;
}

/**
 * Checks if a cost object has any structured data.
 */
export function hasStructuredCost(cost: unknown): cost is BaseCostData {
  const typedCost = cost as BaseCostData;
  return (
    typedCost &&
    typeof typedCost === "object" &&
    (typedCost.openai !== undefined ||
      typedCost.gemini !== undefined ||
      typedCost.searchapi !== undefined ||
      typedCost.google_trends !== undefined)
  );
}

// ======================================================================================|
// MARK: Component

export interface CostSectionInternalProps {
  /** Cost data object with provider breakdowns */
  cost: CostData | unknown;
  /** Optional title override (default: "Cost") */
  title?: string;
  /** Whether to show the section header (default: true) */
  showHeader?: boolean;
}

/**
 * Displays a cost breakdown section with summary and provider details.
 * Internal component for debug/developer use.
 */
export function CostSectionInternal({ cost, title = "Cost", showHeader = true }: CostSectionInternalProps) {
  // Type guard to check if cost has the expected structure
  const typedCost = cost as CostData;
  const hasStructured =
    typedCost &&
    typeof typedCost === "object" &&
    (typedCost.openai || typedCost.gemini || typedCost.searchapi || typedCost.google_trends);

  // If cost doesn't have structured provider data, render raw JSON
  if (!hasStructured) {
    return (
      <DevCardBadge name="CostSectionInternal">
      <div className="border border-gray-200 dark:border-zinc-700 rounded-lg overflow-hidden">
        {showHeader && (
          <div className="bg-gray-100 dark:bg-zinc-800 px-4 py-2 border-b border-gray-200 dark:border-zinc-700">
            <Subheading level={4}>{title}</Subheading>
          </div>
        )}
        <div className="bg-gray-50 dark:bg-zinc-900 p-4 overflow-x-auto">
          <pre className="text-sm text-gray-700 dark:text-gray-300 font-mono">
            {JSON.stringify(cost, null, 2)}
          </pre>
        </div>
      </div>
      </DevCardBadge>
    );
  }

  // Calculate totals from structured cost data
  const openaiCost = typedCost.openai?.total_cost_in_currency ?? 0;
  const geminiCost = typedCost.gemini?.total_cost_in_currency ?? 0;
  const searchapiCost = typedCost.searchapi?.total_cost_in_currency ?? 0;
  const googleTrendsCost = typedCost.google_trends?.total_cost_in_currency ?? 0;
  const totalCost = openaiCost + geminiCost + searchapiCost + googleTrendsCost;
  const totalTokens = (typedCost.openai?.total_tokens ?? 0) + (typedCost.gemini?.total_tokens ?? 0);
  const totalApiRequests = (typedCost.searchapi?.total_requests ?? 0) + (typedCost.google_trends?.total_requests ?? 0);

  return (
    <DevCardBadge name="CostSectionInternal">
    <div className="border border-gray-200 dark:border-zinc-700 rounded-lg overflow-hidden">
      {showHeader && (
        <div className="bg-gray-100 dark:bg-zinc-800 px-4 py-2 border-b border-gray-200 dark:border-zinc-700">
          <Subheading level={4}>{title}</Subheading>
        </div>
      )}
      <div className="p-4 space-y-4">
        {/* Summary */}
        <div className="flex gap-6 flex-wrap">
          <div>
            <Text>Total Cost</Text>
            <p className="text-lg font-semibold font-accent text-zinc-950 dark:text-white">
              ${totalCost.toFixed(4)}
            </p>
          </div>
          {totalTokens > 0 && (
            <div>
              <Text>LLM Tokens</Text>
              <p className="text-lg font-semibold font-accent text-zinc-950 dark:text-white">
                {totalTokens.toLocaleString()}
              </p>
            </div>
          )}
          {totalApiRequests > 0 && (
            <div>
              <Text>Google Trends Requests</Text>
              <p className="text-lg font-semibold font-accent text-zinc-950 dark:text-white">
                {totalApiRequests}
              </p>
            </div>
          )}
        </div>

        {/* Provider Breakdown */}
        <div className="space-y-1">
          {typedCost.openai && (
            <Text>
              <Strong>OpenAI:</Strong> {typedCost.openai.requests.length} requests,{" "}
              {typedCost.openai.total_tokens.toLocaleString()} tokens, $
              {typedCost.openai.total_cost_in_currency.toFixed(4)}
            </Text>
          )}
          {typedCost.gemini && (
            <Text>
              <Strong>Gemini:</Strong> {typedCost.gemini.requests.length} requests,{" "}
              {typedCost.gemini.total_tokens.toLocaleString()} tokens, $
              {typedCost.gemini.total_cost_in_currency.toFixed(4)}
            </Text>
          )}
          {typedCost.searchapi && (
            <Text>
              <Strong>SearchAPI:</Strong> {typedCost.searchapi.total_requests} requests, $
              {typedCost.searchapi.total_cost_in_currency.toFixed(4)}
              {typedCost.searchapi.requests_by_endpoint?.map((ep, idx) => (
                <span key={idx} className="text-zinc-500 dark:text-zinc-400">
                  {" "}
                  ({ep.endpoint_name}: {ep.count})
                </span>
              ))}
            </Text>
          )}
          {typedCost.google_trends && (
            <Text>
              <Strong>Google Trends:</Strong> {typedCost.google_trends.total_requests} requests, $
              {typedCost.google_trends.total_cost_in_currency.toFixed(4)}
              {typedCost.google_trends.requests_by_endpoint?.map((ep, idx) => (
                <span key={idx} className="text-zinc-500 dark:text-zinc-400">
                  {" "}
                  ({ep.endpoint_name}: {ep.count})
                </span>
              ))}
            </Text>
          )}
        </div>
      </div>
    </div>
    </DevCardBadge>
  );
}

// ======================================================================================|
// MARK: EngineCostCard Component

export interface EngineCostCardProps {
  /** The engine identifier */
  engineId: AuditSubEngineId;
  /** Cost data for this engine */
  cost: BaseCostData;
}

/**
 * Displays a compact cost card for a single sub-engine.
 */
export function EngineCostCard({ engineId, cost }: EngineCostCardProps) {
  const label = AUDIT_SUB_ENGINE_LABELS[engineId];
  const totalCost = calculateTotalCost(cost);
  const totalTokens = (cost.openai?.total_tokens ?? 0) + (cost.gemini?.total_tokens ?? 0);
  const totalSearchApiRequests = cost.searchapi?.total_requests ?? 0;
  const totalGoogleTrendsRequests = cost.google_trends?.total_requests ?? 0;
  const totalApiRequests = totalSearchApiRequests + totalGoogleTrendsRequests;

  // Don't render if no cost
  if (totalCost === 0) {
    return null;
  }

  return (
    <DevCardBadge name="EngineCostCard">
    <div className="border border-gray-200 dark:border-zinc-700 rounded-lg p-4 bg-white dark:bg-zinc-900">
      <div className="flex justify-between items-start mb-2">
        <Text className="font-medium text-zinc-700 dark:text-zinc-300">{label}</Text>
        <span className="text-lg font-semibold font-accent text-zinc-950 dark:text-white">
          ${totalCost.toFixed(4)}
        </span>
      </div>
      <div className="space-y-1 text-sm">
        {cost.openai && (
          <Text className="text-zinc-500 dark:text-zinc-400">
            OpenAI: {cost.openai.total_tokens.toLocaleString()} tokens
          </Text>
        )}
        {cost.gemini && (
          <Text className="text-zinc-500 dark:text-zinc-400">
            Gemini: {cost.gemini.total_tokens.toLocaleString()} tokens
          </Text>
        )}
        {totalSearchApiRequests > 0 && (
          <Text className="text-zinc-500 dark:text-zinc-400">
            SearchAPI: {totalSearchApiRequests} requests
          </Text>
        )}
        {totalGoogleTrendsRequests > 0 && (
          <Text className="text-zinc-500 dark:text-zinc-400">
            Google Trends: {totalGoogleTrendsRequests} requests
          </Text>
        )}
        {totalTokens === 0 && totalApiRequests === 0 && (
          <Text className="text-zinc-400 dark:text-zinc-500 italic">No details available</Text>
        )}
      </div>
    </div>
    </DevCardBadge>
  );
}

// ======================================================================================|
// MARK: AuditCostBreakdown Component

export interface AuditCostBreakdownProps {
  /** Complete cost data including engine breakdown */
  cost: CostData | unknown;
}

/**
 * Displays a full audit cost breakdown with total cost and per-engine costs.
 * Used specifically for the Audit Cost subtab.
 */
export function AuditCostBreakdown({ cost }: AuditCostBreakdownProps) {
  const typedCost = cost as CostData;
  const isStructured = hasStructuredCost(typedCost);

  if (!isStructured) {
    return (
      <DevCardBadge name="AuditCostBreakdown">
      <div className="space-y-4">
        <div className="border border-gray-200 dark:border-zinc-700 rounded-lg overflow-hidden">
          <div className="bg-gray-100 dark:bg-zinc-800 px-4 py-2 border-b border-gray-200 dark:border-zinc-700">
            <Subheading level={4}>Cost</Subheading>
          </div>
          <div className="bg-gray-50 dark:bg-zinc-900 p-4 overflow-x-auto">
            <pre className="text-sm text-gray-700 dark:text-gray-300 font-mono">
              {JSON.stringify(cost, null, 2)}
            </pre>
          </div>
        </div>
      </div>
      </DevCardBadge>
    );
  }

  const totalCost = calculateTotalCost(typedCost);
  const totalTokens = (typedCost.openai?.total_tokens ?? 0) + (typedCost.gemini?.total_tokens ?? 0);
  const totalSearchApiRequests = typedCost.searchapi?.total_requests ?? 0;
  const totalGoogleTrendsRequests = typedCost.google_trends?.total_requests ?? 0;
  const totalApiRequests = totalSearchApiRequests + totalGoogleTrendsRequests;

  // Get engine costs if available
  const engineIds: AuditSubEngineId[] = ["icon", "competitor", "keyword", "screenshot", "sentiment", "primaryUseCase", "anchoredBrief"];
  const engines = typedCost.engines;

  return (
    <DevCardBadge name="AuditCostBreakdown">
    <div className="space-y-6">
      {/* Total Cost Summary */}
      <div className="border border-gray-200 dark:border-zinc-700 rounded-lg overflow-hidden">
        <div className="bg-gray-100 dark:bg-zinc-800 px-4 py-2 border-b border-gray-200 dark:border-zinc-700">
          <Subheading level={4}>Total Audit Cost</Subheading>
        </div>
        <div className="p-4">
          <div className="flex gap-6 flex-wrap mb-4">
            <div>
              <Text>Total Cost</Text>
              <p className="text-2xl font-bold font-accent text-zinc-950 dark:text-white">
                ${totalCost.toFixed(4)}
              </p>
            </div>
            {totalTokens > 0 && (
              <div>
                <Text>LLM Tokens</Text>
                <p className="text-lg font-semibold font-accent text-zinc-950 dark:text-white">
                  {totalTokens.toLocaleString()}
                </p>
              </div>
            )}
            {totalApiRequests > 0 && (
              <div>
                <Text>API Requests</Text>
                <p className="text-lg font-semibold font-accent text-zinc-950 dark:text-white">
                  {totalApiRequests}
                </p>
              </div>
            )}
          </div>

          {/* Provider Breakdown */}
          <div className="space-y-1 border-t border-gray-200 dark:border-zinc-700 pt-3">
            <Text className="font-medium mb-2">Provider Breakdown</Text>
            {typedCost.openai && (
              <Text>
                <Strong>OpenAI:</Strong> {typedCost.openai.requests.length} requests,{" "}
                {typedCost.openai.total_tokens.toLocaleString()} tokens, $
                {typedCost.openai.total_cost_in_currency.toFixed(4)}
              </Text>
            )}
            {typedCost.gemini && (
              <Text>
                <Strong>Gemini:</Strong> {typedCost.gemini.requests.length} requests,{" "}
                {typedCost.gemini.total_tokens.toLocaleString()} tokens, $
                {typedCost.gemini.total_cost_in_currency.toFixed(4)}
              </Text>
            )}
            {typedCost.searchapi && (
              <Text>
                <Strong>SearchAPI:</Strong> {typedCost.searchapi.total_requests} requests, $
                {typedCost.searchapi.total_cost_in_currency.toFixed(4)}
              </Text>
            )}
            {typedCost.google_trends && (
              <Text>
                <Strong>Google Trends:</Strong> {typedCost.google_trends.total_requests} requests, $
                {typedCost.google_trends.total_cost_in_currency.toFixed(4)}
              </Text>
            )}
          </div>
        </div>
      </div>

      {/* Per-Engine Cost Breakdown */}
      {engines && Object.keys(engines).length > 0 && (
        <div>
          <Subheading level={4} className="mb-3">
            Cost by Engine
          </Subheading>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {engineIds.map((engineId) => {
              const engineCost = engines[engineId];
              if (!engineCost || !hasStructuredCost(engineCost)) {
                return null;
              }
              return <EngineCostCard key={engineId} engineId={engineId} cost={engineCost} />;
            })}
          </div>
        </div>
      )}
    </div>
    </DevCardBadge>
  );
}
